import { useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import type { VisualizerMode } from '../../types';

interface WaveformVisualizerProps {
  audioData: Uint8Array;
  color?: string;
  mode?: VisualizerMode;
  height?: number;
  barCount?: number;
  className?: string;
  isActive?: boolean;
}

export function WaveformVisualizer({
  audioData,
  color = '#e60000',
  mode = 'bars',
  height = 200,
  barCount = 64,
  className,
  isActive = true,
}: WaveformVisualizerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);
  const smoothedDataRef = useRef<Float32Array>(new Float32Array(barCount));

  const getGradientColor = useCallback(
    (ctx: CanvasRenderingContext2D, y: number, h: number): CanvasGradient => {
      const gradient = ctx.createLinearGradient(0, h, 0, 0);
      gradient.addColorStop(0, color);
      gradient.addColorStop(0.5, '#ff6b35');
      gradient.addColorStop(1, '#ffcc00');
      return gradient;
    },
    [color]
  );

  const drawBars = useCallback(
    (ctx: CanvasRenderingContext2D, data: Uint8Array, w: number, h: number) => {
      const barWidth = (w / barCount) * 0.7;
      const gap = (w / barCount) * 0.3;
      const step = Math.floor(data.length / barCount);

      ctx.fillStyle = getGradientColor(ctx, 0, h);

      for (let i = 0; i < barCount; i++) {
        const value = data[i * step] / 255;
        // Smooth the value
        smoothedDataRef.current[i] += (value - smoothedDataRef.current[i]) * 0.3;
        const barHeight = smoothedDataRef.current[i] * h * 0.85;
        const x = i * (barWidth + gap) + gap / 2;
        const y = (h - barHeight) / 2;

        // Rounded bars
        const radius = barWidth / 2;
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + barWidth - radius, y);
        ctx.quadraticCurveTo(x + barWidth, y, x + barWidth, y + radius);
        ctx.lineTo(x + barWidth, y + barHeight - radius);
        ctx.quadraticCurveTo(x + barWidth, y + barHeight, x + barWidth - radius, y + barHeight);
        ctx.lineTo(x + radius, y + barHeight);
        ctx.quadraticCurveTo(x, y + barHeight, x, y + barHeight - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.fill();
      }
    },
    [barCount, getGradientColor]
  );

  const drawWave = useCallback(
    (ctx: CanvasRenderingContext2D, data: Uint8Array, w: number, h: number) => {
      const step = Math.ceil(data.length / w);
      const amp = h / 2;

      ctx.beginPath();
      ctx.strokeStyle = getGradientColor(ctx, 0, h);
      ctx.lineWidth = 2;

      for (let i = 0; i < w; i++) {
        const dataIndex = Math.min(i * step, data.length - 1);
        const value = data[dataIndex] / 255;
        smoothedDataRef.current[Math.min(i, barCount - 1)] +=
          (value - smoothedDataRef.current[Math.min(i, barCount - 1)]) * 0.2;

        const y = amp - smoothedDataRef.current[Math.min(i, barCount - 1)] * amp * 0.8;

        if (i === 0) {
          ctx.moveTo(i, y);
        } else {
          const prevX = i - 1;
          const prevDataIndex = Math.min(prevX * step, data.length - 1);
          const prevValue = data[prevDataIndex] / 255;
          const prevY = amp - prevValue * amp * 0.8;
          const cpX = (prevX + i) / 2;
          ctx.quadraticCurveTo(cpX, prevY, i, y);
        }
      }

      ctx.stroke();

      // Mirror wave
      ctx.beginPath();
      ctx.strokeStyle = getGradientColor(ctx, 0, h);
      ctx.globalAlpha = 0.3;
      for (let i = 0; i < w; i++) {
        const dataIndex = Math.min(i * step, data.length - 1);
        const value = data[dataIndex] / 255;
        const y = amp + value * amp * 0.8;

        if (i === 0) {
          ctx.moveTo(i, y);
        } else {
          const prevX = i - 1;
          const prevDataIndex = Math.min(prevX * step, data.length - 1);
          const prevValue = data[prevDataIndex] / 255;
          const prevY = amp + prevValue * amp * 0.8;
          const cpX = (prevX + i) / 2;
          ctx.quadraticCurveTo(cpX, prevY, i, y);
        }
      }
      ctx.stroke();
      ctx.globalAlpha = 1;
    },
    [barCount, getGradientColor]
  );

  const drawCircular = useCallback(
    (ctx: CanvasRenderingContext2D, data: Uint8Array, w: number, h: number) => {
      const centerX = w / 2;
      const centerY = h / 2;
      const radius = Math.min(w, h) * 0.3;
      const bars = Math.min(128, data.length);
      const angleStep = (Math.PI * 2) / bars;

      for (let i = 0; i < bars; i++) {
        const value = data[i] / 255;
        const smoothed = smoothedDataRef.current[i % barCount] +=
          (value - smoothedDataRef.current[i % barCount]) * 0.3;
        const barHeight = smoothed * radius * 0.8;
        const angle = i * angleStep - Math.PI / 2;

        const x1 = centerX + Math.cos(angle) * radius;
        const y1 = centerY + Math.sin(angle) * radius;
        const x2 = centerX + Math.cos(angle) * (radius + barHeight);
        const y2 = centerY + Math.sin(angle) * (radius + barHeight);

        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.strokeStyle = getGradientColor(ctx, 0, h);
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      // Inner circle
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius * 0.8, 0, Math.PI * 2);
      ctx.strokeStyle = color + '40';
      ctx.lineWidth = 1;
      ctx.stroke();
    },
    [barCount, color, getGradientColor]
  );

  const drawSpectrum = useCallback(
    (ctx: CanvasRenderingContext2D, data: Uint8Array, w: number, h: number) => {
      const barWidth = w / barCount;
      const step = Math.floor(data.length / barCount);

      for (let i = 0; i < barCount; i++) {
        const value = data[i * step] / 255;
        smoothedDataRef.current[i] += (value - smoothedDataRef.current[i]) * 0.3;
        const barHeight = smoothedDataRef.current[i] * h;

        // Color based on height
        const hue = 0 + (value * 40);
        const saturation = 80 + value * 20;
        const lightness = 40 + value * 30;
        ctx.fillStyle = `hsl(${hue}, ${saturation}%, ${lightness}%)`;

        const x = i * barWidth;
        const y = h - barHeight;

        ctx.fillRect(x, y, barWidth - 1, barHeight);
      }
    },
    [barCount]
  );

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Handle high DPI
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const w = rect.width;
    const h = rect.height;

    // Clear with fade effect
    ctx.fillStyle = 'rgba(10, 10, 15, 0.3)';
    ctx.fillRect(0, 0, w, h);

    if (!isActive || audioData.length === 0) {
      // Draw idle state
      drawIdleState(ctx, w, h, mode);
      animationRef.current = requestAnimationFrame(draw);
      return;
    }

    switch (mode) {
      case 'bars':
        drawBars(ctx, audioData, w, h);
        break;
      case 'wave':
        drawWave(ctx, audioData, w, h);
        break;
      case 'circular':
        drawCircular(ctx, audioData, w, h);
        break;
      case 'spectrum':
        drawSpectrum(ctx, audioData, w, h);
        break;
    }

    animationRef.current = requestAnimationFrame(draw);
  }, [audioData, mode, isActive, drawBars, drawWave, drawCircular, drawSpectrum]);

  const drawIdleState = (
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    mode: VisualizerMode
  ) => {
    const time = Date.now() * 0.001;
    ctx.strokeStyle = color + '30';
    ctx.lineWidth = 2;

    if (mode === 'circular') {
      const centerX = w / 2;
      const centerY = h / 2;
      const radius = Math.min(w, h) * 0.3;

      // Draw pulsing rings
      for (let i = 0; i < 3; i++) {
        const r = radius + Math.sin(time + i * 0.5) * 5;
        ctx.beginPath();
        ctx.arc(centerX, centerY, r, 0, Math.PI * 2);
        ctx.strokeStyle = color + `${15 + i * 10}`;
        ctx.stroke();
      }
    } else {
      // Draw flat line with subtle wave
      ctx.beginPath();
      ctx.strokeStyle = color + '30';
      for (let x = 0; x < w; x++) {
        const y = h / 2 + Math.sin(x * 0.02 + time) * 3;
        if (x === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
  };

  useEffect(() => {
    animationRef.current = requestAnimationFrame(draw);
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [draw]);

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: `${height}px`,
          borderRadius: '12px',
        }}
        className="bg-black/20"
      />
    </motion.div>
  );
}
